import { createRenderFeature } from './data-render-feature.js';
import { kmlSourceSchema } from '../shared/kml-source.js';
import type { DrawingLayer } from '../shared/drawing-layer.js';
import type { MapFileState } from '../shared/map-file-state.js';
import { readLayerFileStructure } from './layer-file-structure.js';
import { dataImageSchema } from '../shared/data-image.js';
import { validDataPolygonGeometry } from '../shared/polygon-validation.js';
import { t } from './i18n.js';
import type { SchemaInput } from '../shared/schema-input.js';
import { z } from 'zod';
import {
  MAX_DATA_LAYER_FEATURES,
  validateDataLayerPayload,
  dataGeometrySchema,
  dataLayerProvenanceSchema,
  importSourceFilesSchema,
  type ImportSourceFile,
  dataLayerStyleSchema,
  coordinateFieldsSchema,
  calculateDataLayerBounds,
  dataLayerPayload,
  randomDataId,
  type DataFeature,
  type DataLayer,
  type DataLayerSourceFormat,
} from '../shared/data-layer.js';

export interface DataLayerImportReport {
  map?: MapFileState;
  layerDefinitions?: DataLayer[];
  drawingLayers?: DrawingLayer[];
  layer: DataLayer | null;
  acceptedFeatures: number;
  warnings?: string[];
  rejectedFeatures: Array<{ index: number; reason: 'invalid_feature' | 'unsupported_geometry' | 'invalid_geometry' }>;
}

interface DataLayerImportOptions {
  name?: string; id?: string; position?: number; color?: string; sourceFormat?: DataLayerSourceFormat; sourceFiles?: ImportSourceFile[];
}

const featureSchema = z.object({ kmlSource: kmlSourceSchema.optional(), coordinateFields: coordinateFieldsSchema.optional(), drawonamapVisibility: z.boolean().optional(), drawonamapSourceStyle: dataLayerStyleSchema.partial().optional(), drawonamapLayerId: z.string().max(100).optional(), visible: z.boolean().optional(), image: dataImageSchema.optional(), type: z.literal('Feature'), id: z.union([z.string(), z.number()]).optional(), geometry: dataGeometrySchema.nullable(), bbox: z.array(z.number()).optional(), properties: z.record(z.string(), z.json()).nullable().optional(), sourceStyle: dataLayerStyleSchema.partial().optional(), drawonamapProvenance: dataLayerProvenanceSchema.optional() }).loose();
const geoJsonSchema = z.union([featureSchema, dataGeometrySchema.transform((geometry) => ({ type: 'Feature' as const, geometry, properties: {} })), z.object({ type: z.literal('FeatureCollection'), features: z.array(z.unknown()), bbox: z.array(z.number()).optional(), drawonamapSourceFiles: importSourceFilesSchema.optional(), drawonamapProvenance: dataLayerProvenanceSchema.optional() }).loose()]);

function importedDataFeature(candidateFeature: z.infer<typeof featureSchema>): DataFeature {
  const feature = createRenderFeature(candidateFeature, randomDataId('feature'));
  if (candidateFeature.drawonamapLayerId) feature.importLayerId = candidateFeature.drawonamapLayerId;
  if (candidateFeature.kmlSource) feature.kmlSource = candidateFeature.kmlSource;
  if (candidateFeature.coordinateFields) feature.coordinateFields = candidateFeature.coordinateFields;
  if (candidateFeature.drawonamapProvenance) feature.provenance = candidateFeature.drawonamapProvenance;
  if (candidateFeature.properties === null) feature.nullProperties = true;
  if (candidateFeature.bbox) feature.bbox = candidateFeature.bbox;
  return feature;
}

function restoreGeoJsonMetadata(layer: DataLayer, input: z.infer<typeof geoJsonSchema>): void {
  const collectionProvenance = input.type === 'FeatureCollection' ? input.drawonamapProvenance : undefined;
  if (input.type === 'FeatureCollection' && input.bbox) layer.bbox = input.bbox;
  const provenance = collectionProvenance ?? layer.features[0]?.provenance;
  if (provenance && layer.features.every((feature) => JSON.stringify(feature.provenance ?? collectionProvenance) === JSON.stringify(provenance))) {
    layer.provenance = provenance;
    for (const feature of layer.features) delete feature.provenance;
  } else if (collectionProvenance) {
    for (const feature of layer.features) feature.provenance ??= collectionProvenance;
  }
}

function createImportedLayer(features: DataFeature[], options: DataLayerImportOptions): DataLayer {
  const color = options.color ?? '#5b4bff';
  return {
    id: options.id ?? randomDataId('layer'), name: options.name ?? 'Imported data', sourceFormat: options.sourceFormat ?? 'geojson',
    style: { color, width: 3, opacity: 1, fillColor: color, fillOpacity: 0.2 }, visible: true, position: options.position ?? 0,
    featureCount: features.length, bounds: calculateDataLayerBounds(features), saved: false, features,
  };
}

export function importGeoJsonDataLayer(value: SchemaInput, options: DataLayerImportOptions = {}): DataLayerImportReport {
  const input = geoJsonSchema.safeParse(value);
  if (!input.success) return { layer: null, acceptedFeatures: 0, rejectedFeatures: [{ index: 0, reason: 'invalid_feature' }] };
  const source: SchemaInput[] = input.data.type === 'Feature' ? [input.data] : input.data.features;
  if (source.length > MAX_DATA_LAYER_FEATURES) throw new Error(`GeoJSON has more than the ${MAX_DATA_LAYER_FEATURES.toLocaleString('en-US')} feature import limit.`);
  const features: DataFeature[] = [];
  const rejectedFeatures: DataLayerImportReport['rejectedFeatures'] = [];
  const warnings: string[] = [];

  source.forEach((candidate, index) => {
    const parsedFeature = featureSchema.safeParse(candidate);
    if (!parsedFeature.success) {
      rejectedFeatures.push({ index, reason: 'invalid_feature' });
      return;
    }
    const feature = importedDataFeature(parsedFeature.data);
    const color = options.color ?? '#5b4bff';
    const testLayer: DataLayer = {
      id: options.id ?? 'validation-layer', name: options.name ?? 'Data', sourceFormat: options.sourceFormat ?? 'geojson',
      style: { color }, visible: true, position: options.position ?? 0, featureCount: 1,
      bounds: calculateDataLayerBounds([feature]), saved: false, features: [feature],
    };
    const validation = validateDataLayerPayload(dataLayerPayload(testLayer));
    if (!validation.success) {
      if (validation.code === 'payload_limit') {
        const error = new Error(validation.errors.join(' '));
        error.name = 'ImportLimitError';
        throw error;
      }
      rejectedFeatures.push({ index, reason: 'invalid_geometry' });
      return;
    }
    if (feature.geometry && !validDataPolygonGeometry(feature.geometry)) {
      warnings.push(t('import_results.polygon_warning').replace('{id}', String(feature.sourceKey ?? index + 1)));
    }
    features.push(feature);
  });

  const sourceFiles = input.data.type === 'FeatureCollection' ? input.data.drawonamapSourceFiles ?? options.sourceFiles : options.sourceFiles;
  const structure: DataLayerImportReport = { layer: null, acceptedFeatures: features.length, rejectedFeatures };
  readLayerFileStructure(value, structure);
  if (!features.length && !sourceFiles?.length && !structure.map && !structure.layerDefinitions?.length) return structure;
  const layer = createImportedLayer(features, options);
  if (sourceFiles?.length) layer.sourceFiles = sourceFiles;
  restoreGeoJsonMetadata(layer, input.data);
  const validation = validateDataLayerPayload(dataLayerPayload(layer), { reuseFeatures: true });
  if (!validation.success) {
    const error = new Error(validation.errors.join(' '));
    if (validation.code === 'payload_limit') error.name = 'ImportLimitError';
    throw error;
  }
  const report: DataLayerImportReport = { ...structure, layer };
  if (warnings.length) report.warnings = warnings;
  return report;
}
